import { useQuery, useQueryClient } from '@tanstack/react-query'
import { v2GetPortfolioQueueOptions } from '@workspace/api-backend/react-query'
import { Loader2, Lock } from 'lucide-react'
import { useState } from 'react'
import { useInvalidateHomeState } from '../../home/hooks/use-home-state'
import { outreachApi, portfolioContactsQueryKey } from '../api'
import { useLaunchFlow } from '../context/launch-flow-context'
import {
  CONTACTS_BOARD_ID,
  QueuedApprovalDialog,
  RosterKanban,
  type RosterMatch,
} from './outreach-active-view'
import { OutreachSetupCta } from './outreach-setup-cta'

// How many real founder cards are revealed while the list is still locked behind
// the profile step — a carrot identical to the investor board.
const LOCKED_PREVIEW_COUNT = 2

type PortfolioStatus = {
  msg1: string | null
  msg2: string | null
  msg3: string | null
  // Prompt version per message — drives legacy-vs-v2 rendering in the dialog.
  msg1PromptVersion?: string | null
  msg2PromptVersion?: string | null
  msg3PromptVersion?: string | null
  messagesReviewedAt: string | null
  contactApprovedAt: string | null
  campaignId: number | null
  contactsCount: number
  // True while investor-portfolio research is still discovering founders, so the
  // Queued count is an estimate (more contacts are still landing). Optional to
  // stay resilient to a stale generated client — the live API always sends it.
  researchInProgress?: boolean
}

// The pre-launch "Who we'll contact" board for the portfolio-founders channel,
// rendered with the SAME RosterKanban as the investor board so both tabs share
// one roster look (Queued / In progress / Finished). The global launch panel
// drives the unified launch + message review, so this board is purely the
// preview + per-contact approve/decline. Decisions are persisted server-side
// (keyed by contact) so they survive a refresh and apply at launch.
export function PortfolioPreLaunchBoard({
  startupId,
  portfolioStatus,
  contactsEstimated = false,
}: {
  startupId: string
  portfolioStatus: PortfolioStatus
  contactsEstimated?: boolean
}) {
  const queryClient = useQueryClient()
  const { checklist } = useLaunchFlow()
  const [detail, setDetail] = useState<RosterMatch | null>(null)
  // Optimistic per-card approve/decline state, keyed by contactId (leadId).
  const [resolutions, setResolutions] = useState<
    Record<string, 'approving' | 'declining' | 'approved' | 'declined'>
  >({})

  const researchDone = portfolioStatus.contactsCount > 0
  // Still discovering founders: keep the Queued count as a "~" estimate and show
  // the "preparing more" activity footer even once the first cards land — exactly
  // like the live roster / investor board. Falsy until the API reports research.
  const researching = portfolioStatus.researchInProgress ?? false

  const contactsQuery = useQuery({
    queryKey: portfolioContactsQueryKey(startupId),
    queryFn: () => outreachApi.portfolioGetContacts(startupId),
    enabled: researchDone,
  })
  const contacts = contactsQuery.data ?? []

  // Approving a founder changes what three separate surfaces say: this board,
  // the queue the sidebar badges count, and Home's card. These calls go through
  // `outreachApi` directly rather than useMutation, so the app-wide
  // refresh-Home-after-any-mutation hook never sees them — the sidebar sat on
  // its old counts while the founder worked through the board.
  const invalidateHome = useInvalidateHomeState(startupId)
  const invalidate = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: portfolioContactsQueryKey(startupId) }),
      queryClient.invalidateQueries({
        queryKey: v2GetPortfolioQueueOptions({ path: { id: startupId } }).queryKey,
      }),
      invalidateHome(),
    ])
  }

  // Shared message preview (startup-level msg1/2/3) — drives the detail dialog
  // conversation for every founder, mirroring the investor dialog.
  const previewMessages = {
    msg1: portfolioStatus.msg1,
    msg2: portfolioStatus.msg2,
    msg3: portfolioStatus.msg3,
    msg1PromptVersion: portfolioStatus.msg1PromptVersion,
    msg2PromptVersion: portfolioStatus.msg2PromptVersion,
    msg3PromptVersion: portfolioStatus.msg3PromptVersion,
  }

  const entries: RosterMatch[] = contacts.map((c) => ({
    investorId: c.fundName,
    leadId: c.contactId,
    contactId: c.contactId,
    investorName: c.fundName,
    contactName: c.name,
    contactTitle: c.title,
    contactScope: null,
    group: 'queued',
    stage: 'Awaiting approval',
    charges: { views: 0, returnVisits: 0, calls: 0 },
    linkedinUrl: c.linkedinUrl,
    companyName: c.companyName,
    fundName: c.fundName,
    approvalState:
      c.decision === 'APPROVED' ? 'approved' : c.decision === 'DECLINED' ? 'declined' : undefined,
    previewMessages,
  }))

  // Locked until the profile step is done: show the top few founders as a carrot,
  // the rest blurred behind the unlock CTA — and hide all approve/decline controls
  // until unlocked (mirrors the investor board).
  const locked = !checklist.steps.some((step) => step.id === 'profile' && step.done)
  const visible = locked ? entries.slice(0, LOCKED_PREVIEW_COUNT) : entries
  // Total founder reach for the "see all N" copy: the research estimate, falling
  // back to the materialized count once cards exist.
  const totalContacts = Math.max(portfolioStatus.contactsCount, entries.length)
  const hiddenCount = Math.max(totalContacts - visible.length, 0)

  // Card badge state for the Queued column, keyed by leadId — server decision,
  // overlaid with any in-flight optimistic state.
  const cardResolutions: Record<string, 'approving' | 'declining' | 'approved' | 'declined'> = {}
  for (const entry of visible) {
    if (!entry.leadId) continue
    const optimistic = resolutions[entry.leadId]
    if (optimistic) cardResolutions[entry.leadId] = optimistic
    else if (entry.approvalState) cardResolutions[entry.leadId] = entry.approvalState
  }

  const decide = (action: 'approve' | 'decline') => (entry: RosterMatch) => {
    const { leadId, contactId } = entry
    if (!leadId || !contactId) return
    setResolutions((p) => ({ ...p, [leadId]: action === 'approve' ? 'approving' : 'declining' }))
    outreachApi
      .portfolioSetContactDecisions(startupId, [
        { contactId, decision: action === 'approve' ? 'APPROVED' : 'DECLINED' },
      ])
      .then(() => {
        setResolutions((p) => ({ ...p, [leadId]: action === 'approve' ? 'approved' : 'declined' }))
        void invalidate()
      })
      .catch(() =>
        setResolutions((p) => {
          const next = { ...p }
          delete next[leadId]
          return next
        })
      )
  }

  // Visible contacts not yet decided (no server decision, no in-flight state).
  // Locked → nothing is approvable (controls are hidden until the profile is done).
  const pendingEntries = locked
    ? []
    : visible.filter((e) => e.leadId && !e.approvalState && !resolutions[e.leadId])

  const approveAll = () => {
    if (pendingEntries.length === 0) return
    setResolutions((p) => {
      const next = { ...p }
      for (const e of pendingEntries) if (e.leadId) next[e.leadId] = 'approving'
      return next
    })
    outreachApi
      .portfolioApproveContacts(startupId)
      .then(() => {
        setResolutions((p) => {
          const next = { ...p }
          for (const e of pendingEntries) if (e.leadId) next[e.leadId] = 'approved'
          return next
        })
        void invalidate()
      })
      .catch(() =>
        setResolutions((p) => {
          const next = { ...p }
          for (const e of pendingEntries) if (e.leadId) delete next[e.leadId]
          return next
        })
      )
  }

  return (
    // Same anchor as the investor board so the hero's "Contacts to reach →"
    // scrolls here too; only one board is mounted at a time.
    <section id={CONTACTS_BOARD_ID} className="mb-10 scroll-mt-24 space-y-5">
      <RosterKanban
        columns={[
          {
            tone: 'primary',
            title: 'Queued',
            subtitle: "Founders we'll reach out to for a warm intro.",
            // While research is still running the count is an estimate ("~N"),
            // even after the first founders land; it firms up once research ends.
            totalCount: researching || contactsEstimated ? `~${totalContacts}` : totalContacts,
            entries: visible,
            hideStatePill: true,
            onCardClick: setDetail,
            // Locked → no approve/decline controls until the profile step is done.
            onApprove: locked ? undefined : decide('approve'),
            onDecline: locked ? undefined : decide('decline'),
            onApproveAll: !locked && pendingEntries.length > 0 ? approveAll : undefined,
            approveAllPending: pendingEntries.length,
            resolutions: cardResolutions,
            lockedCount: locked && researchDone ? hiddenCount : 0,
            footer:
              researching || contactsQuery.isLoading ? (
                <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
                  <Loader2 className="size-5 animate-spin text-muted-foreground/60" />
                  <p className="text-[12px] text-muted-foreground">
                    {portfolioStatus.contactsCount === 0
                      ? 'Scanning investor portfolios for founder contacts…'
                      : `Found ${portfolioStatus.contactsCount} founders so far, preparing more…`}
                  </p>
                </div>
              ) : locked && hiddenCount > 0 ? (
                <div className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-3 text-center">
                  <div className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-foreground">
                    <Lock className="size-3.5 text-primary" />
                    {hiddenCount} more founders locked
                  </div>
                  <p className="mt-1 text-[11.5px] leading-relaxed text-muted-foreground">
                    Complete your profile to see all {totalContacts} founder contacts, each with a
                    ready-to-send warm intro.
                  </p>
                  <div className="mt-3 flex justify-center">
                    <OutreachSetupCta />
                  </div>
                </div>
              ) : undefined,
          },
          {
            tone: 'blue',
            title: 'In progress',
            subtitle: 'Messages start going out once your campaign is live.',
            totalCount: 0,
            entries: [],
          },
          {
            tone: 'muted',
            title: 'Finished',
            subtitle: 'Founders whose sequence has wrapped up.',
            totalCount: 0,
            entries: [],
          },
        ]}
      />

      <QueuedApprovalDialog
        investor={detail}
        messages={detail?.previewMessages ?? null}
        track="portfolio"
        onClose={() => setDetail(null)}
        onApprove={(entry) => {
          decide('approve')(entry)
          setDetail(null)
        }}
        onDecline={(entry) => {
          decide('decline')(entry)
          setDetail(null)
        }}
        onRemove={() => setDetail(null)}
      />
    </section>
  )
}
